import { useEffect, useMemo, useState } from 'react';

import { useRouteLoaderData } from '@remix-run/react';
import type { Price, PricingFormMode, Tier } from '@shared/dream-components';

import useEmail from '~/hooks/useEmail';
import useStripeSession from '~/hooks/useStripeSession';
import type { loader } from '~/root';

import { UpgradeContext } from './UpgradeContext';

interface UpgradeProviderProps {
  children: React.ReactNode;
  tiers: Tier[];
  giftTiers: Tier[];
  isInternalCheckout: boolean;
  recommendedTierId?: string;
  recommendedPriceId?: string;
}

const getSelectedTierAndPrice = (tiers?: Tier[], recommendedTierId?: string, recommendedPriceId?: string) => {
  if (!tiers) return {};
  if (Array.isArray(tiers) && tiers.length === 0) return {};

  // If there's a recommended price ID but no tier ID, search across all tiers for that price
  if (recommendedPriceId && !recommendedTierId) {
    for (const tier of tiers) {
      const recommendedPrice = tier.prices.find((price) => price.id === recommendedPriceId);
      if (recommendedPrice) {
        return {
          selectedTier: tier,
          selectedPrice: recommendedPrice,
        };
      }
    }
  }

  // If there's a recommended tier ID, try to find it
  if (recommendedTierId) {
    const recommendedTier = tiers.find((tier) => tier.id === recommendedTierId);
    if (recommendedTier) {
      // If there's a recommended price ID, try to find it within the recommended tier
      if (recommendedPriceId) {
        const recommendedPrice = recommendedTier.prices.find((price) => price.id === recommendedPriceId);
        if (recommendedPrice) {
          return {
            selectedTier: recommendedTier,
            selectedPrice: recommendedPrice,
          };
        }
      }

      // If no specific price is recommended, use the first price of the recommended tier
      if (Array.isArray(recommendedTier.prices) && recommendedTier.prices.length > 0) {
        return {
          selectedTier: recommendedTier,
          selectedPrice: recommendedTier.prices[0],
        };
      }

      return { selectedTier: recommendedTier };
    }
  }

  // Fallback to first tier if no recommended tier is found or specified
  const firstTier = tiers[0];
  if (Array.isArray(firstTier.prices) && firstTier.prices.length === 0) return { selectedTier: firstTier };

  return {
    selectedTier: firstTier,
    selectedPrice: firstTier.prices[0],
  };
};

export const UpgradeProvider: React.FC<UpgradeProviderProps> = ({
  children,
  tiers,
  giftTiers,
  isInternalCheckout,
  recommendedTierId,
  recommendedPriceId,
}) => {
  const { publication } = useRouteLoaderData<typeof loader>('root');

  // Only load Stripe if there are non-free tiers
  const shouldLoadStripe = useMemo(() => tiers.some((tier) => tier.id !== 'free'), [tiers]);
  const { stripeSession, isLoadingStripeSession } = useStripeSession({
    publication,
    shouldLoadStripe,
  });

  const [email, setEmail] = useEmail();
  const [taxId, setTaxId] = useState('');
  const [donationAmountCents, setDonationAmountCents] = useState(0);
  const [selectedTier, setSelectedTier] = useState<Tier | undefined>();
  const [selectedPrice, setSelectedPrice] = useState<Price | undefined>();
  const [selectedGiftTier, setSelectedGiftTier] = useState<Tier | undefined>();
  const [selectedGiftPrice, setSelectedGiftPrice] = useState<Price | undefined>();
  const [currentFormMode, setCurrentFormMode] = useState<PricingFormMode>('individual');

  // Set tier/price first time
  useEffect(() => {
    if (selectedTier || selectedPrice) return;

    const { selectedTier: initialSelectedTier, selectedPrice: initialSelectedPrice } = getSelectedTierAndPrice(
      tiers,
      recommendedTierId,
      recommendedPriceId,
    );

    if (initialSelectedTier && initialSelectedPrice) {
      setSelectedTier(initialSelectedTier);
      setSelectedPrice(initialSelectedPrice);
    } else if (initialSelectedTier) {
      setSelectedTier(initialSelectedTier);
      setSelectedPrice(initialSelectedTier.prices[0]);
    }
  }, [selectedPrice, selectedTier, tiers, recommendedTierId, recommendedPriceId]);

  // Set gift tier/price first time
  useEffect(() => {
    if (selectedGiftTier || selectedGiftPrice) return;

    const { selectedTier: initialSelectedGiftTier, selectedPrice: initialSelectedGiftPrice } = getSelectedTierAndPrice(
      giftTiers,
      recommendedTierId,
      recommendedPriceId,
    );

    if (initialSelectedGiftTier && initialSelectedGiftPrice) {
      setSelectedGiftTier(initialSelectedGiftTier);
      setSelectedGiftPrice(initialSelectedGiftPrice);
    } else if (initialSelectedGiftTier) {
      setSelectedGiftTier(initialSelectedGiftTier);
      setSelectedGiftPrice(initialSelectedGiftTier.prices[0]);
    }
  }, [selectedGiftPrice, selectedGiftTier, giftTiers, recommendedTierId, recommendedPriceId]);

  const isStripeLoading = isLoadingStripeSession;

  return (
    <UpgradeContext.Provider
      value={{
        email,
        setEmail,
        showTaxIdField: isInternalCheckout ? !!publication?.subscriber_tax_id_collection_enabled : false,
        taxId,
        setTaxId,
        donationAmountCents,
        setDonationAmountCents,
        selectedTier,
        setSelectedTier,
        selectedPrice,
        setSelectedPrice,
        selectedGiftTier,
        setSelectedGiftTier,
        selectedGiftPrice,
        setSelectedGiftPrice,
        isStripeLoading,
        stripeSession,
        isInternalCheckout,
        currentFormMode,
        setCurrentFormMode,
      }}
    >
      {children}
    </UpgradeContext.Provider>
  );
};
